import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Button, Select, Row, Col, Statistic, Tag, Table,
    Progress, Spin, message, Alert, Space, Badge, Divider,
    Collapse, Tooltip, DatePicker
} from 'antd';
import {
    FilePdfOutlined, FileWordOutlined, FilterOutlined,
    DownloadOutlined, ReloadOutlined, PrinterOutlined,
    CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined
} from '@ant-design/icons';
import MeService from '../../services/MeService';
import moment from 'moment';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Option }  = Select;
const { Panel }   = Collapse;

// ── Constants ─────────────────────────────────────────────────────────────
const SDG3_TARGETS = [
    { code:'3.1',   label:'Reduce maternal mortality' },
    { code:'3.1.1', label:'Maternal mortality ratio' },
    { code:'3.2',   label:'End preventable child deaths' },
    { code:'3.2.1', label:'Under-five mortality rate' },
    { code:'3.2.2', label:'Neonatal mortality rate' },
    { code:'3.3',   label:'End AIDS, TB, malaria & NTDs' },
    { code:'3.3.1', label:'HIV incidence rate' },
    { code:'3.3.2', label:'TB incidence rate' },
    { code:'3.4',   label:'Reduce NCDs & promote mental health' },
    { code:'3.4.2', label:'Suicide mortality rate' },
    { code:'3.5.2', label:'Alcohol per capita consumption' },
    { code:'3.6.1', label:'Road traffic mortality rate' },
    { code:'3.7',   label:'Universal SRH access' },
    { code:'3.7.2', label:'Adolescent birth rate' },
    { code:'3.8',   label:'Universal health coverage' },
    { code:'3.8.1', label:'UHC Service Coverage Index' },
    { code:'3.8.2', label:'Out-of-pocket health expenditure' },
    { code:'3.9',   label:'Deaths from pollution & WASH' },
    { code:'3.b',   label:'Access to medicines & vaccines' },
    { code:'3.c',   label:'Health workforce' },
    { code:'3.c.1', label:'Health worker density' },
    { code:'3.d',   label:'Health security capacity' },
    { code:'3.d.1', label:'IHR core capacity score' },
];

const REGIONS = ['Hhohho', 'Manzini', 'Lubombo', 'Shiselweni'];

const TREND_ICON = (pct) => {
    if (pct === null || pct === undefined) return { icon: '—',   color: '#888' };
    if (pct >= 75) return { icon: '✅', color: '#52c41a' };
    if (pct >= 50) return { icon: '🟡', color: '#fa8c16' };
    if (pct >= 25) return { icon: '🔴', color: '#f5222d' };
    return            { icon: '⚫', color: '#8c8c8c' };
};

const STATUS_LABEL = (pct) => {
    if (pct === null || pct === undefined) return 'No Data';
    if (pct >= 75) return 'On Track';
    if (pct >= 50) return 'At Risk';
    if (pct >= 25) return 'Off Track';
    return 'Not Started';
};

// ── Shared "UN SDG report style" presentation helpers ───────────────────────
// A small set of reusable pieces modelled on The Sustainable Development
// Goals Report 2025's own house style: a bullet-first summary before any
// narrative prose, small "Note:" captions under charts/tables explaining
// methodology or caveats, and a consistent "Source:" attribution line.
// Centralising these means every section that adopts them looks and reads
// the same way, rather than each section inventing its own convention.
function KeyFindings({ items }) {
    if (!items || !items.length) return null;
    return (
        <ul style={{ margin:'0 0 14px', paddingLeft:18, fontSize:12, lineHeight:1.85, color:'#333' }}>
            {items.map((it, i) => <li key={i} style={{ marginBottom:4 }}>{it}</li>)}
        </ul>
    );
}

function ChartNote({ children }) {
    if (!children) return null;
    return (
        <div style={{ fontSize:10.5, color:'#888', marginTop:6, lineHeight:1.5, fontStyle:'italic' }}>
            Note: {children}
        </div>
    );
}

function SourceLine({ children }) {
    if (!children) return null;
    return (
        <div style={{ fontSize:10, color:'#aaa', marginTop:2, lineHeight:1.5 }}>
            Source: {children}
        </div>
    );
}

// ── Achievement calculator ─────────────────────────────────────────────────
// For indicators where lower = better (mortality rates), reverse the calculation
const LOWER_IS_BETTER = [
    'mortality', 'death', 'stillbirth', 'incidence', 'out-of-pocket',
    'unmet', 'unemployment', 'violence', 'stunting', 'wasting', 'overweight',
    'road traffic', 'suicide', 'alcohol', 'poverty', 'zero-dose'
];

const isLowerBetter = (title) => {
    const t = (title || '').toLowerCase();
    return LOWER_IS_BETTER.some(k => t.includes(k));
};

const calcAchievement = (actual, target, baseline, title) => {
    const a = parseFloat(actual);
    const t = parseFloat(target);
    const b = parseFloat(baseline);
    if (isNaN(a) || isNaN(t)) return null;

    if (isLowerBetter(title)) {
        // Lower is better: achievement = how much reduction achieved vs target reduction
        if (!isNaN(b) && b !== t) {
            const reduction_needed  = b - t;
            const reduction_achieved = b - a;
            if (reduction_needed <= 0) return a <= t ? 100 : 0;
            return Math.min(100, Math.max(0, Math.round(reduction_achieved / reduction_needed * 100)));
        }
        return a <= t ? 100 : Math.max(0, Math.round((t / a) * 100));
    } else {
        // Higher is better
        if (t === 0) return 100;
        return Math.min(100, Math.max(0, Math.round((a / t) * 100)));
    }
};

// ── Indicator Status Row ───────────────────────────────────────────────────
function IndicatorRow({ ind, progress, period, compact=false }) {
    const actual     = progress?.actual_value;
    const target     = ind.target_value;
    const baseline   = ind.baseline_value;
    const achievement= calcAchievement(actual, target, baseline, ind.title);
    const trend      = TREND_ICON(achievement);
    const statusLbl  = STATUS_LABEL(achievement);

    return (
        <div style={{
            display:'flex', alignItems:'center', gap:10,
            padding: compact ? '6px 10px' : '10px 14px',
            marginBottom:4,
            background: achievement === null ? '#fafafa' :
                        achievement >= 75 ? '#f6ffed' :
                        achievement >= 50 ? '#fff7e6' :
                        achievement >= 25 ? '#fff2f0' : '#f5f5f5',
            borderRadius:6,
            borderLeft:`3px solid ${trend.color}`,
            border:`1px solid ${
                achievement >= 75 ? '#b7eb8f' :
                achievement >= 50 ? '#ffd591' :
                achievement >= 25 ? '#ffa39e' : '#d9d9d9'}`,
        }}>
            <div style={{ fontSize:18, flexShrink:0 }}>{trend.icon}</div>

            <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize: compact?11:12, fontWeight:600, color:'#1f3864',
                    overflow:'hidden', textOverflow:'ellipsis',
                    whiteSpace: compact?'nowrap':'normal' }}>
                    {ind.code && <span style={{ color:'#888', marginRight:6, fontSize:10 }}>{ind.code}</span>}
                    {ind.title}
                </div>
                {!compact && ind.data_source && (
                    <div style={{ fontSize:10, color:'#aaa', marginTop:1 }}>
                        Source: {ind.data_source}
                    </div>
                )}
            </div>

            {/* Values */}
            <div style={{ display:'flex', gap:16, alignItems:'center', flexShrink:0, fontSize:11 }}>
                <div style={{ textAlign:'center' }}>
                    <div style={{ color:'#888', fontSize:9 }}>BASELINE</div>
                    <div style={{ fontWeight:700, color:'#555' }}>{baseline || '—'}</div>
                </div>
                <div style={{ textAlign:'center' }}>
                    <div style={{ color:'#888', fontSize:9 }}>TARGET</div>
                    <div style={{ fontWeight:700, color:'#1890ff' }}>{target || '—'}</div>
                </div>
                <div style={{ textAlign:'center' }}>
                    <div style={{ color:'#888', fontSize:9 }}>ACTUAL</div>
                    <div style={{ fontWeight:700, color: trend.color }}>
                        {actual || <span style={{ color:'#ccc' }}>—</span>}
                    </div>
                </div>
                {achievement !== null && (
                    <div style={{ width:70 }}>
                        <Progress size="small" percent={achievement}
                            strokeColor={trend.color} showInfo={false}/>
                        <div style={{ fontSize:10, textAlign:'center', color:trend.color, fontWeight:700 }}>
                            {achievement}%
                        </div>
                    </div>
                )}
                <Tag color={
                    achievement === null ? 'default' :
                    achievement >= 75 ? 'green' :
                    achievement >= 50 ? 'orange' : 'red'
                } style={{ fontSize:10, margin:0, whiteSpace:'nowrap' }}>
                    {statusLbl}
                </Tag>
                {progress?.verification_status === 'verified' && (
                    <Tooltip title="Verified by M&E officer">
                        <CheckCircleOutlined style={{ color:'#52c41a', fontSize:14 }}/>
                    </Tooltip>
                )}
            </div>
        </div>
    );
}

// ── SDG Target Group ───────────────────────────────────────────────────────
function SdgTargetGroup({ target, indicators, progressMap, period }) {
    const targetIndicators = indicators.filter(i =>
        (i.responsible_role || '').includes(target.code)
    );
    if (targetIndicators.length === 0) return null;

    const withData   = targetIndicators.filter(i => progressMap[i.id]?.actual_value);
    const achievements = targetIndicators
        .map(i => calcAchievement(
            progressMap[i.id]?.actual_value,
            i.target_value, i.baseline_value, i.title
        ))
        .filter(a => a !== null);
    const avgAchievement = achievements.length
        ? Math.round(achievements.reduce((a,b)=>a+b,0)/achievements.length) : null;
    const trend = TREND_ICON(avgAchievement);

    return (
        <Card size="small" style={{
            marginBottom:12,
            borderLeft:`4px solid ${trend.color}`,
            background: avgAchievement === null ? '#fafafa' :
                        avgAchievement >= 75 ? '#f6ffed' :
                        avgAchievement >= 50 ? '#fff7e6' : '#fff2f0',
        }}>
            {/* Target header */}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                <div style={{ fontSize:22 }}>{trend.icon}</div>
                <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <Tag color="blue" style={{ fontWeight:800, fontSize:12 }}>
                            SDG {target.code}
                        </Tag>
                        <span style={{ fontWeight:700, fontSize:13, color:'#1f3864' }}>
                            {target.label}
                        </span>
                    </div>
                    <div style={{ fontSize:11, color:'#888', marginTop:2 }}>
                        {targetIndicators.length} indicator{targetIndicators.length!==1?'s':''} ·
                        {withData.length} with reported data
                    </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                    {avgAchievement !== null ? (
                        <>
                            <div style={{ fontSize:22, fontWeight:800, color:trend.color }}>
                                {avgAchievement}%
                            </div>
                            <div style={{ fontSize:10, color:'#888' }}>avg achievement</div>
                        </>
                    ) : (
                        <Tag>No data yet</Tag>
                    )}
                </div>
            </div>

            {/* Indicators */}
            {targetIndicators.map(ind => (
                <IndicatorRow
                    key={ind.id}
                    ind={ind}
                    progress={progressMap[ind.id]}
                    period={period}
                    compact={targetIndicators.length > 3}
                />
            ))}
        </Card>
    );
}

// ── PDF Generator ──────────────────────────────────────────────────────────
const generatePDF = (indicators, progressMap, period, filterRegion, sdgStats) => {
    const doc = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });
    const now = moment().format('DD MMMM YYYY');
    const pageW = doc.internal.pageSize.getWidth();
    let y = 0;

    const addPage = () => { doc.addPage(); y = 20; };
    const checkPage = (needed=30) => { if (y + needed > 270) addPage(); };

    // Cover
    doc.setFillColor(31, 56, 100);
    doc.rect(0, 0, pageW, 60, 'F');
    doc.setTextColor(255,255,255);
    doc.setFontSize(10);
    doc.text('KINGDOM OF ESWATINI · MINISTRY OF HEALTH', pageW/2, 18, { align:'center' });
    doc.setFontSize(16);
    doc.setFont('helvetica','bold');
    doc.text('NHSSP 2024-2028', pageW/2, 30, { align:'center' });
    doc.setFontSize(13);
    doc.text('SDG Health Progress Report', pageW/2, 40, { align:'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica','normal');
    doc.text(`Reporting Period: ${period || 'All periods'}  |  Generated: ${now}`, pageW/2, 52, { align:'center' });

    // Summary strip
    y = 70;
    doc.setTextColor(31,56,100);
    doc.setFontSize(13);
    doc.setFont('helvetica','bold');
    doc.text('NATIONAL SUMMARY', 14, y);
    y += 8;

    const sdgIndicators  = indicators.filter(i => i.responsible_role?.startsWith('SDG'));
    const withData       = sdgIndicators.filter(i => progressMap[i.id]?.actual_value);
    const onTrack        = sdgIndicators.filter(i => {
        const a = calcAchievement(progressMap[i.id]?.actual_value, i.target_value, i.baseline_value, i.title);
        return a !== null && a >= 75;
    }).length;
    const atRisk = sdgIndicators.filter(i => {
        const a = calcAchievement(progressMap[i.id]?.actual_value, i.target_value, i.baseline_value, i.title);
        return a !== null && a >= 50 && a < 75;
    }).length;
    const offTrack = sdgIndicators.filter(i => {
        const a = calcAchievement(progressMap[i.id]?.actual_value, i.target_value, i.baseline_value, i.title);
        return a !== null && a < 50;
    }).length;

    // Bullet-style key findings, mirroring the UN SDG report's lead-with-bullets
    // convention, ahead of the detailed section-by-section tables below.
    doc.setFont('helvetica','normal');
    doc.setFontSize(9);
    doc.setTextColor(60,60,60);
    const coveragePct = sdgIndicators.length ? Math.round(withData.length/sdgIndicators.length*100) : 0;
    const bullets = [
        `${sdgIndicators.length} SDG-mapped indicators are tracked this period, of which ${withData.length} (${coveragePct}%) have reported data.`,
        `${onTrack} indicator${onTrack!==1?'s':''} ${onTrack!==1?'are':'is'} on track (≥75% achievement); ${atRisk} at risk (50–74%); ${offTrack} off track (<50%).`,
        withData.length < sdgIndicators.length
            ? `${sdgIndicators.length-withData.length} indicator${sdgIndicators.length-withData.length!==1?'s':''} still ${sdgIndicators.length-withData.length!==1?'have':'has'} no reported value for ${period||'this period'} and are shown against baseline/target only.`
            : 'All SDG-mapped indicators have at least one reported value for this period.',
    ];
    bullets.forEach(b => {
        const lines = doc.splitTextToSize(`•  ${b}`, pageW - 32);
        doc.text(lines, 14, y);
        y += lines.length * 4.2 + 1.5;
    });
    y += 4;

    doc.autoTable({
        startY: y,
        head: [['Total SDG Indicators','With Reported Data','On Track (≥75%)','At Risk (50-74%)','Off Track (<50%)']],
        body: [[
            sdgIndicators.length,
            `${withData.length} (${Math.round(withData.length/sdgIndicators.length*100)||0}%)`,
            `${onTrack} ✅`,
            `${atRisk} 🟡`,
            `${offTrack} 🔴`,
        ]],
        headStyles: { fillColor:[31,56,100], textColor:255, fontStyle:'bold', fontSize:9 },
        bodyStyles: { fontSize:10, fontStyle:'bold', halign:'center' },
        columnStyles: {
            2: { textColor:[30,107,60] },
            3: { textColor:[217,119,6] },
            4: { textColor:[192,57,43] },
        },
        margin: { left:14, right:14 },
    });

    y = doc.lastAutoTable.finalY + 6;
    doc.setFontSize(8);
    doc.setFont('helvetica','italic');
    doc.setTextColor(140,140,140);
    doc.text('Note: Achievement % reflects progress from baseline toward target for the current reporting period; indicators with no baseline are scored against target only.', 14, y, { maxWidth: pageW-28 });
    y += 10;

    // SDG Section
    checkPage(20);
    doc.setFillColor(31,56,100);
    doc.rect(14, y, pageW-28, 8, 'F');
    doc.setTextColor(255,255,255);
    doc.setFontSize(11);
    doc.setFont('helvetica','bold');
    doc.text('SECTION 1: SDG 3 TARGET PROGRESS', 18, y+5.5);
    y += 14;

    // Group by SDG target
    const targetGroups = {};
    sdgIndicators.forEach(ind => {
        const sdgMatch = (ind.responsible_role || '').match(/SDG ([\d.a-z]+)/i);
        if (sdgMatch) {
            const key = sdgMatch[1];
            if (!targetGroups[key]) targetGroups[key] = [];
            targetGroups[key].push(ind);
        }
    });

    Object.entries(targetGroups).forEach(([targetCode, inds]) => {
        checkPage(40);
        const targetDef = SDG3_TARGETS.find(t => t.code === targetCode);

        const rows = inds.map(ind => {
            const p   = progressMap[ind.id];
            const ach = calcAchievement(p?.actual_value, ind.target_value, ind.baseline_value, ind.title);
            const status = STATUS_LABEL(ach);
            return [
                ind.code || '',
                ind.title.length > 55 ? ind.title.slice(0,55)+'…' : ind.title,
                ind.baseline_value || '—',
                ind.target_value   || '—',
                p?.actual_value    || '—',
                ach !== null ? `${ach}%` : '—',
                status,
            ];
        });

        doc.setFontSize(10);
        doc.setFont('helvetica','bold');
        doc.setTextColor(46,117,182);
        doc.text(`SDG ${targetCode}: ${targetDef?.label || ''}`, 14, y);
        y += 2;

        doc.autoTable({
            startY: y,
            head: [['Code','Indicator','Baseline','Target','Actual','Achievement','Status']],
            body: rows,
            headStyles: { fillColor:[46,117,182], textColor:255, fontSize:8, fontStyle:'bold' },
            bodyStyles: { fontSize:8, textColor:[50,50,50] },
            columnStyles: {
                0: { cellWidth:14 },
                1: { cellWidth:72 },
                2: { cellWidth:18, halign:'center' },
                3: { cellWidth:18, halign:'center' },
                4: { cellWidth:18, halign:'center' },
                5: { cellWidth:18, halign:'center', fontStyle:'bold' },
                6: { cellWidth:20, halign:'center' },
            },
            didDrawCell: (data) => {
                if (data.column.index === 6 && data.section === 'body') {
                    const val = data.cell.text[0];
                    if (val === 'On Track')    data.cell.styles.textColor = [30,107,60];
                    if (val === 'At Risk')     data.cell.styles.textColor = [217,119,6];
                    if (val === 'Off Track')   data.cell.styles.textColor = [192,57,43];
                }
            },
            margin: { left:14, right:14 },
        });

        y = doc.lastAutoTable.finalY + 8;
        checkPage(10);
    });

    // NHSSP-only section
    const nhsspOnly = indicators.filter(i =>
        !i.responsible_role?.startsWith('SDG') || i.responsible_role === ''
    );

    if (nhsspOnly.length > 0) {
        checkPage(20);
        doc.setFillColor(30,107,60);
        doc.rect(14, y, pageW-28, 8, 'F');
        doc.setTextColor(255,255,255);
        doc.setFontSize(11);
        doc.setFont('helvetica','bold');
        doc.text('SECTION 2: NHSSP OPERATIONAL INDICATORS (Non-SDG)', 18, y+5.5);
        y += 14;

        // Group by domain
        const domainGroups = {};
        nhsspOnly.forEach(ind => {
            const domain = ind.section_code?.slice(0,2) || 'D?';
            if (!domainGroups[domain]) domainGroups[domain] = [];
            domainGroups[domain].push(ind);
        });

        Object.entries(domainGroups).forEach(([domain, inds]) => {
            checkPage(30);
            const rows = inds.map(ind => {
                const p   = progressMap[ind.id];
                const ach = calcAchievement(p?.actual_value, ind.target_value, ind.baseline_value, ind.title);
                return [
                    ind.code || '',
                    ind.title.length > 55 ? ind.title.slice(0,55)+'…' : ind.title,
                    ind.baseline_value || '—',
                    ind.target_value   || '—',
                    p?.actual_value    || '—',
                    ach !== null ? `${ach}%` : '—',
                    STATUS_LABEL(ach),
                ];
            });

            doc.setFontSize(9);
            doc.setFont('helvetica','bold');
            doc.setTextColor(30,107,60);
            doc.text(domain, 14, y);
            y += 2;

            doc.autoTable({
                startY: y,
                head: [['Code','Indicator','Baseline','Target','Actual','Achievement','Status']],
                body: rows,
                headStyles: { fillColor:[30,107,60], textColor:255, fontSize:8 },
                bodyStyles: { fontSize:8 },
                columnStyles: {
                    0: { cellWidth:14 },
                    1: { cellWidth:72 },
                    2: { cellWidth:18, halign:'center' },
                    3: { cellWidth:18, halign:'center' },
                    4: { cellWidth:18, halign:'center' },
                    5: { cellWidth:18, halign:'center', fontStyle:'bold' },
                    6: { cellWidth:20, halign:'center' },
                },
                margin: { left:14, right:14 },
            });
            y = doc.lastAutoTable.finalY + 6;
        });
    }

    // Footer on all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(150,150,150);
        doc.setFont('helvetica','normal');
        doc.text(
            `Eswatini Ministry of Health · QoC Platform · NHSSP SDG Progress Report · ${now} · Page ${p} of ${totalPages}`,
            pageW/2, 287, { align:'center' }
        );
    }

    doc.save(`Eswatini_SDG_Health_Progress_Report_${moment().format('YYYY-MM-DD')}.pdf`);
    message.success('PDF report downloaded');
};

// ── Word (docx) export via server ──────────────────────────────────────────
const generateWord = async (indicators, progressMap, period) => {
    try {
        const payload = {
            period,
            indicators: indicators.map(ind => ({
                ...ind,
                actual_value:          progressMap[ind.id]?.actual_value || null,
                achievement_pct:       calcAchievement(
                    progressMap[ind.id]?.actual_value,
                    ind.target_value, ind.baseline_value, ind.title
                ),
                verification_status:   progressMap[ind.id]?.verification_status || null,
                progress_narrative:    progressMap[ind.id]?.progress_narrative  || null,
            })),
        };
        const r = await MeService.generateSdgWordReport(payload);
        if (r.data?.code !== 200) throw new Error(r.data?.message);
        // Server returns a download URL
        const url = r.data?.data?.url;
        if (url) {
            const a = document.createElement('a');
            a.href = url; a.download = `Eswatini_SDG_Health_Progress_Report_${moment().format('YYYY-MM-DD')}.docx`;
            a.click();
            message.success('Word report downloaded');
        }
    } catch(e) {
        message.error('Word export failed — ' + (e.message || 'Server error'));
    }
};

// ── Main Page ─────────────────────────────────────────────────────────────
export default function MeSdgReport() {
    const [indicators,   setIndicators]   = useState([]);
    const [progressMap,  setProgressMap]  = useState({});
    const [periods,      setPeriods]      = useState([]);
    const [frameworks,   setFrameworks]   = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [generating,   setGenerating]   = useState('');
    const [period,       setPeriod]       = useState('');
    const [filterFw,     setFilterFw]     = useState('');
    const [filterRegion, setFilterRegion] = useState('');
    const [filterSdg,    setFilterSdg]    = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [loaded,       setLoaded]       = useState(false);
    const printRef = useRef();

    useEffect(() => {
        Promise.all([
            MeService.getFrameworks({ status:'active' }),
            MeService.getMePeriods(),
        ]).then(([fwRes, pRes]) => {
            setFrameworks(fwRes.data?.data || []);
            const ps = pRes.data?.data || [];
            setPeriods(ps);
            const cur = ps.find(p => p.is_current == 1);
            if (cur) setPeriod(cur.label);
        }).catch(() => message.error('Failed to load'));
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const [indRes, progRes] = await Promise.all([
                MeService.getIndicators({
                    framework_id: filterFw || 1, // default to NHSSP framework
                }),
                MeService.getProgress({
                    framework_id:     filterFw || 1,
                    reporting_period: period || undefined,
                    ...(filterRegion ? { region: filterRegion } : {}),
                }),
            ]);

            const inds = indRes.data?.data || [];
            setIndicators(inds);

            const pmap = {};
            (progRes.data?.data || []).forEach(p => {
                // Keep best/latest record per indicator
                if (!pmap[p.indicator_id] || p.verification_status === 'verified') {
                    pmap[p.indicator_id] = p;
                }
            });
            setProgressMap(pmap);
            setLoaded(true);
        } catch(e) { message.error('Failed to load report data'); }
        finally { setLoading(false); }
    };

    // Stats
    const sdgIndicators  = indicators.filter(i => (i.responsible_role || '').startsWith('SDG'));
    const nhsspOnly      = indicators.filter(i => !(i.responsible_role || '').startsWith('SDG'));
    const withData       = indicators.filter(i => progressMap[i.id]?.actual_value);
    const sdgWithData    = sdgIndicators.filter(i => progressMap[i.id]?.actual_value);

    const achievements   = indicators
        .map(i => calcAchievement(progressMap[i.id]?.actual_value, i.target_value, i.baseline_value, i.title))
        .filter(a => a !== null);
    const avgAchievement = achievements.length
        ? Math.round(achievements.reduce((a,b)=>a+b,0)/achievements.length) : null;

    const onTrack  = achievements.filter(a => a >= 75).length;
    const atRisk   = achievements.filter(a => a >= 50 && a < 75).length;
    const offTrack = achievements.filter(a => a < 50).length;

    // Filter indicators for display
    const filteredSdg = sdgIndicators.filter(i => {
        if (filterSdg && !(i.responsible_role || '').includes(filterSdg)) return false;
        if (filterStatus) {
            const ach = calcAchievement(progressMap[i.id]?.actual_value, i.target_value, i.baseline_value, i.title);
            if (filterStatus === 'on_track'  && ach < 75)  return false;
            if (filterStatus === 'at_risk'   && (ach < 50 || ach >= 75)) return false;
            if (filterStatus === 'off_track' && ach >= 50) return false;
            if (filterStatus === 'no_data'   && ach !== null) return false;
        }
        return true;
    });

    // Group filtered SDG indicators by target
    const sdgGrouped = {};
    filteredSdg.forEach(ind => {
        const match = (ind.responsible_role || '').match(/SDG ([\d.a-z]+)/i);
        if (match) {
            const k = match[1];
            if (!sdgGrouped[k]) sdgGrouped[k] = [];
            sdgGrouped[k].push(ind);
        }
    });

    const handleGeneratePdf = async () => {
        setGenerating('pdf');
        try {
            await new Promise(res => setTimeout(res, 100));
            generatePDF(indicators, progressMap, period, filterRegion);
        } finally { setGenerating(''); }
    };

    const handleGenerateWord = async () => {
        setGenerating('word');
        try {
            await generateWord(indicators, progressMap, period);
        } finally { setGenerating(''); }
    };

    // ── Key Findings bullets for the landing summary — mirrors the UN SDG
    // report's Goal-page convention of leading every section with 3-4
    // plain-language bullets before any chart or table.
    const coveragePct = indicators.length ? Math.round(withData.length/indicators.length*100) : 0;
    const keyFindingsBullets = loaded ? [
        <>Of <strong>{indicators.length} tracked indicators</strong>, <strong>{withData.length} ({coveragePct}%)</strong> have reported data for {period || 'this period'}.</>,
        <><strong>{onTrack}</strong> indicator{onTrack!==1?'s':''} {onTrack!==1?'are':'is'} on track (≥75% achievement), <strong>{atRisk}</strong> at risk (50–74%), and <strong>{offTrack}</strong> off track (&lt;50%).</>,
        avgAchievement !== null
            ? <>The average achievement across indicators with reported data is <strong>{avgAchievement}%</strong>.</>
            : <>No indicators have reported data yet for {period || 'this period'} — figures below show baseline and target only.</>,
        indicators.length - withData.length > 0
            ? <><strong>{indicators.length - withData.length}</strong> indicator{indicators.length-withData.length!==1?'s':''} still {indicators.length-withData.length!==1?'need':'needs'} facilities to log progress in the M&E Tracker.</>
            : null,
    ].filter(Boolean) : [];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin:0 }}>🌐 SDG Health Progress Report</h4>
                    <small className="text-muted">
                        NHSSP 2024-2028 · SDG 3 implementation tracking · Eswatini
                    </small>
                </div>
                {loaded && (
                    <div className="my-auto ml-auto" style={{ display:'flex', gap:8 }}>
                        <Button icon={<FilePdfOutlined/>}
                            loading={generating === 'pdf'}
                            onClick={handleGeneratePdf}
                            style={{ background:'#c0392b', borderColor:'#c0392b', color:'#fff' }}>
                            Download PDF
                        </Button>
                        <Button icon={<FileWordOutlined/>}
                            loading={generating === 'word'}
                            onClick={handleGenerateWord}
                            style={{ background:'#2e75b6', borderColor:'#2e75b6', color:'#fff' }}>
                            Download Word
                        </Button>
                    </div>
                )}
            </div>

            <div className="page-pad">
                {/* Controls */}
                <Card size="small" style={{ marginBottom:16 }}>
                    <Row gutter={12} align="middle">
                        <Col xs={24} sm={6}>
                            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>Framework</div>
                            <Select value={filterFw || 1} onChange={setFilterFw}
                                style={{ width:'100%' }}>
                                {frameworks.map(f => (
                                    <Option key={f.id} value={f.id}>
                                        {f.code} — {f.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={5}>
                            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>Reporting Period</div>
                            <Select value={period} onChange={setPeriod}
                                style={{ width:'100%' }} placeholder="Select period">
                                {periods.map(p => (
                                    <Option key={p.label} value={p.label}>
                                        {p.is_current==1?'★ ':''}{p.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={4}>
                            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>Region</div>
                            <Select value={filterRegion} onChange={setFilterRegion}
                                style={{ width:'100%' }} allowClear placeholder="All regions">
                                {REGIONS.map(r => <Option key={r} value={r}>{r}</Option>)}
                            </Select>
                        </Col>
                        <Col xs={24} sm={4} style={{ display:'flex', alignItems:'flex-end' }}>
                            <Button type="primary" icon={<ReloadOutlined/>}
                                style={{ width:'100%', background:'#1f3864', borderColor:'#1f3864' }}
                                onClick={load} loading={loading}>
                                Generate Report
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {loading && (
                    <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
                )}

                {!loading && !loaded && (
                    <Card style={{ textAlign:'center', padding:60 }}>
                        <div style={{ fontSize:48, marginBottom:16 }}>🌐</div>
                        <div style={{ fontSize:18, fontWeight:700, color:'#1f3864', marginBottom:8 }}>
                            SDG Health Progress Report
                        </div>
                        <div style={{ color:'#888', marginBottom:24, maxWidth:500, margin:'0 auto 24px' }}>
                            Select a framework and reporting period, then click Generate Report to see
                            Eswatini's progress against SDG 3 targets and NHSSP 2024-2028 indicators.
                        </div>
                        <Button type="primary" size="large" icon={<ReloadOutlined/>}
                            style={{ background:'#1f3864', borderColor:'#1f3864' }}
                            onClick={load}>
                            Generate Report
                        </Button>
                    </Card>
                )}

                {!loading && loaded && (
                    <>
                        {/* Key Findings — bullet-first summary, UN SDG report style,
                            ahead of the stat strip and detailed sections below. */}
                        <Card size="small" style={{ marginBottom:16, borderLeft:'4px solid #1f3864', background:'#f8faff' }}>
                            <div style={{ fontWeight:800, fontSize:13, color:'#1f3864', marginBottom:8, textTransform:'uppercase', letterSpacing:0.5 }}>
                                Key Findings
                            </div>
                            <KeyFindings items={keyFindingsBullets}/>
                        </Card>

                        {/* National summary strip */}
                        <Row gutter={12} style={{ marginBottom:8 }}>
                            {[
                                { label:'Total Indicators',    val:indicators.length,    color:'#1f3864' },
                                { label:'SDG-Mapped',          val:sdgIndicators.length, color:'#1890ff' },
                                { label:'With Reported Data',  val:withData.length,      color:'#7c3aed' },
                                { label:'On Track ≥75%',       val:onTrack,              color:'#52c41a' },
                                { label:'At Risk 50-74%',      val:atRisk,               color:'#fa8c16' },
                                { label:'Off Track <50%',      val:offTrack,             color:'#f5222d' },
                                { label:'Avg Achievement',     val: avgAchievement !== null ? `${avgAchievement}%` : '—', color:'#1f3864' },
                                { label:'Data Coverage',       val:`${Math.round(withData.length/indicators.length*100)||0}%`, color:'#0d6e6e' },
                            ].map(k => (
                                <Col key={k.label} xs={12} sm={6} md={3}>
                                    <Card size="small" style={{ textAlign:'center', borderLeft:`4px solid ${k.color}`, marginBottom:8 }}>
                                        <div style={{ fontSize:18, fontWeight:800, color:k.color }}>{k.val}</div>
                                        <div style={{ fontSize:9, color:'#888' }}>{k.label}</div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                        <ChartNote>Achievement % measures progress from baseline toward target for the current reporting period. Indicators with no baseline are scored against target only.</ChartNote>
                        <div style={{ marginBottom:16 }}/>

                        {/* Data coverage alert */}
                        {withData.length < indicators.length * 0.5 && (
                            <Alert type="warning" showIcon style={{ marginBottom:16 }}
                                message={`Only ${withData.length} of ${indicators.length} indicators have reported data for ${period || 'this period'}`}
                                description="Facilities need to log progress in the M&E Tracker before this report can show actual achievement values. Indicators without data are shown with baseline and target only."
                            />
                        )}

                        {/* Filters */}
                        <Card size="small" style={{ marginBottom:12 }}>
                            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                                <span style={{ fontSize:12, fontWeight:600, color:'#555' }}>Filter:</span>
                                <Select value={filterSdg} onChange={setFilterSdg}
                                    style={{ width:200 }} allowClear placeholder="All SDG targets">
                                    {SDG3_TARGETS.map(t => (
                                        <Option key={t.code} value={t.code}>
                                            SDG {t.code} — {t.label}
                                        </Option>
                                    ))}
                                </Select>
                                <Select value={filterStatus} onChange={setFilterStatus}
                                    style={{ width:160 }} allowClear placeholder="All statuses">
                                    <Option value="on_track">✅ On Track (≥75%)</Option>
                                    <Option value="at_risk">🟡 At Risk (50-74%)</Option>
                                    <Option value="off_track">🔴 Off Track (&lt;50%)</Option>
                                    <Option value="no_data">⚫ No Data</Option>
                                </Select>
                                <Tag style={{ marginLeft:'auto' }}>
                                    {filteredSdg.length} SDG indicators · {nhsspOnly.length} NHSSP-only
                                </Tag>
                                <Button size="small" icon={<FilePdfOutlined/>}
                                    loading={generating==='pdf'}
                                    onClick={handleGeneratePdf}
                                    style={{ background:'#c0392b', borderColor:'#c0392b', color:'#fff' }}>
                                    PDF
                                </Button>
                                <Button size="small" icon={<FileWordOutlined/>}
                                    loading={generating==='word'}
                                    onClick={handleGenerateWord}
                                    style={{ background:'#2e75b6', borderColor:'#2e75b6', color:'#fff' }}>
                                    Word
                                </Button>
                            </div>
                        </Card>

                        {/* ── SECTION 1: SDG-Mapped Indicators ── */}
                        <div style={{
                            background:'#1f3864', borderRadius:8,
                            padding:'10px 16px', marginBottom:6,
                            display:'flex', alignItems:'center', gap:10
                        }}>
                            <span style={{ fontSize:20 }}>🌐</span>
                            <div style={{ flex:1 }}>
                                <div style={{ fontWeight:800, fontSize:15, color:'#fff' }}>
                                    Section 1 — SDG 3 Target Progress
                                </div>
                                <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}>
                                    {sdgIndicators.length} indicators mapped to {Object.keys(sdgGrouped).length} SDG 3 targets ·
                                    {sdgWithData.length} with reported data
                                </div>
                            </div>
                            <div style={{ textAlign:'right' }}>
                                {avgAchievement !== null && (
                                    <div style={{ fontSize:24, fontWeight:800, color:
                                        avgAchievement>=75?'#52c41a':avgAchievement>=50?'#fa8c16':'#f5222d' }}>
                                        {avgAchievement}%
                                    </div>
                                )}
                                <div style={{ fontSize:10, color:'rgba(255,255,255,0.6)' }}>
                                    national avg
                                </div>
                            </div>
                        </div>
                        <div style={{ marginBottom:12 }}>
                            <SourceLine>M&E Tracker, reporting period {period || 'all periods'}{filterRegion ? `, ${filterRegion} Region` : ''}.</SourceLine>
                        </div>

                        {Object.entries(sdgGrouped)
                            .sort((a,b) => {
                                const pa = parseFloat(a[0].replace(/[^0-9.]/g,''));
                                const pb = parseFloat(b[0].replace(/[^0-9.]/g,''));
                                return pa - pb;
                            })
                            .map(([targetCode, inds]) => {
                                const targetDef = SDG3_TARGETS.find(t => t.code === targetCode);
                                return (
                                    <SdgTargetGroup
                                        key={targetCode}
                                        target={{ code:targetCode, label:targetDef?.label || targetCode }}
                                        indicators={inds}
                                        progressMap={progressMap}
                                        period={period}
                                    />
                                );
                            })
                        }

                        {filteredSdg.length === 0 && (
                            <Card style={{ textAlign:'center', padding:30, marginBottom:16 }}>
                                <div style={{ color:'#888' }}>No SDG indicators match the current filter.</div>
                            </Card>
                        )}

                        {/* ── SECTION 2: NHSSP-Only Indicators ── */}
                        {nhsspOnly.length > 0 && (
                            <>
                                <div style={{
                                    background:'#1e6b3c', borderRadius:8,
                                    padding:'10px 16px', marginBottom:6, marginTop:24,
                                    display:'flex', alignItems:'center', gap:10
                                }}>
                                    <span style={{ fontSize:20 }}>📋</span>
                                    <div>
                                        <div style={{ fontWeight:800, fontSize:15, color:'#fff' }}>
                                            Section 2 — NHSSP Operational Indicators
                                        </div>
                                        <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}>
                                            {nhsspOnly.length} indicators specific to NHSSP 2024-2028 (governance, systems, financing — not directly mapped to SDG 3)
                                        </div>
                                    </div>
                                </div>
                                <div style={{ marginBottom:12 }}>
                                    <ChartNote>Domains follow the NHSSP 2024–2028 results framework: D1 Health System Investments, D2 Outputs, D3 Service Coverage, D4 Other Determinants, D5 Health System Impact.</ChartNote>
                                </div>

                                <Collapse ghost>
                                    {['D1','D2','D3','D4','D5'].map(domain => {
                                        const domainInds = nhsspOnly.filter(i =>
                                            (i.section_code || i.code || '').startsWith(domain)
                                        );
                                        if (!domainInds.length) return null;
                                        const domainAchs = domainInds
                                            .map(i => calcAchievement(progressMap[i.id]?.actual_value, i.target_value, i.baseline_value, i.title))
                                            .filter(a => a !== null);
                                        const domainAvg = domainAchs.length
                                            ? Math.round(domainAchs.reduce((a,b)=>a+b,0)/domainAchs.length) : null;
                                        const tl = TREND_ICON(domainAvg);

                                        return (
                                            <Panel
                                                key={domain}
                                                header={
                                                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                                        <span>{tl.icon}</span>
                                                        <span style={{ fontWeight:700 }}>
                                                            {domain} — {
                                                                {D1:'Health System Investments',D2:'Health System Outputs',
                                                                 D3:'Health Service Coverage',D4:'Other Determinants',
                                                                 D5:'Health System Impact'}[domain]
                                                            }
                                                        </span>
                                                        <Tag>{domainInds.length} indicators</Tag>
                                                        {domainAvg !== null && (
                                                            <Tag color={domainAvg>=75?'green':domainAvg>=50?'orange':'red'}>
                                                                {domainAvg}% avg
                                                            </Tag>
                                                        )}
                                                    </div>
                                                }
                                            >
                                                {domainInds.map(ind => (
                                                    <IndicatorRow
                                                        key={ind.id}
                                                        ind={ind}
                                                        progress={progressMap[ind.id]}
                                                        period={period}
                                                        compact={domainInds.length > 5}
                                                    />
                                                ))}
                                            </Panel>
                                        );
                                    })}
                                </Collapse>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}